import { Injectable } from '@angular/core';
import { MessageService } from '@services/helpers/message-service/message.service';
import {
	Contract,
	ListItem,
	PageRequest,
	Product,
	Quotes
} from '@models/models';
import { ValidationProvider } from '@services/helpers/validation.service';
import { QuoteUpdateService } from '@services/quote/quote-update.service';
import { StringModifierServiceProvider } from '@services/helpers/string-modifier-service/stringModifier.service';
import {
	QtBaseData,
	QtItemPayload,
	QtItemType,
	QTSaveDataPayload,
	QtTemplateListItem,
	QtTemplateType
} from '@models/qt-templates.model';
import { InvoiceServiceProvider } from '@services/invoice/invoice-service/invoice.service';
import { PaymentsServiceProvider } from '@services/payments/payments-service/payments.service';
import { Invoice } from '@models/invoice.model';
import { OrderByNamePipe } from '@pipes/order-by/order-by-name';

@Injectable()
export class QuoteInvoiceTemplatesService {
	constructor(
		private messageService: MessageService,
		private validationService: ValidationProvider,
		private quoteUpdateService: QuoteUpdateService,
		private stringModifierService: StringModifierServiceProvider,
		private invoiceUpdateService: InvoiceServiceProvider,
		private paymentsService: PaymentsServiceProvider,
		private orderByName: OrderByNamePipe
	) {}

	static getTemplatesParameters(): PageRequest {
		return {
			paging: {
				pageSize: 1000,
				pageNumber: 0
			},
			sortCriterias: [{ fieldName: 'name', sortAscending: true }]
		};
	}

	static getEmptyQtItem(
		type: QtItemType,
		product: Product,
		introduction?: string
	): QtItemPayload {
		return {
			contractTemplate: null,
			createdAt: null,
			generateInvoiceWhenAccepted: true,
			introduction: introduction || '',
			notes: '',
			name: '',
			paymentSchedule: null,
			paymentsRule: {
				strategy: 'NO_SPLIT_PAYMENTS',
				paymentSchedule: null
			},
			products: product ? [product] : [],
			questionnaireTemplate: null,
			summary: null,
			type
		};
	}

	createTemplateDataFromQuote(
		quote: Quotes,
		contract: Contract,
		type: QtItemType
	): QtItemPayload {
		return {
			introduction: quote.introduction,
			notes: quote.notes,
			contractTemplate: contract,
			questionnaireTemplate: quote.questionnaire,
			paymentSchedule: quote.paymentSchedule
				? {
						name: quote.paymentSchedule.name,
						id: 0
				  }
				: null,
			paymentsRule: quote.paymentsRule,
			payments: quote.payments,
			generateInvoiceWhenAccepted: quote.generateInvoiceWhenAccepted,
			summary: quote.summary,
			name: null,
			createdAt: null,
			products: quote.products,
			type
		};
	}

	createTemplateDataFromInvoice(
		invoice: Invoice,
		contract: Contract,
		type: QtItemType
	): QtItemPayload {
		return {
			notes: invoice.notes || '',
			contractTemplate: contract,
			questionnaireTemplate: invoice.questionnaire || null,
			paymentSchedule: invoice.paymentSchedule
				? {
						name: invoice.paymentSchedule.name,
						id: 0
				  }
				: null,
			paymentsRule: invoice.paymentsRule,
			payments: invoice.payments,
			summary: invoice.summary,
			name: null,
			createdAt: null,
			products: invoice.summary ? [...invoice.summary.items] : [],
			type
		};
	}

	prepareProductsForDiff(products: Array<Product>): Array<Product> {
		return products.map((product: Product): Product => ({
			...product,
			summary: null,
			order: null,
			amount: null,
			quantity: +product.quantity,
			discount: +product.discount,
			price: +product.price
		}));
	}

	prepareProductsForDiffInvoices(products: Array<Product>): Array<Product> {
		return products.map((product: Product): Product => ({
			...product,
			summary: null,
			order: null,
			amount: null,
			mandatory: null,
			recommended: null,
			quantity: +product.quantity,
			discount: +product.discount,
			price: +product.price
		}));
	}

	prepareQuoteInvoiceDataForDiff(
		invoice: Partial<QtItemPayload>
	): Partial<QtItemPayload> {
		const newInvoice = JSON.parse(JSON.stringify(invoice));
		newInvoice.products = newInvoice.products.map(product => ({
			...product,
			taxType: product.type === 'GIFT' ? null : product.taxType,
			type: product.type === 'GIFT' ? 'GIFT' : null
		}));
		return newInvoice;
	}

	prepareTemplateForSave(templateData: Partial<QtBaseData>): QTSaveDataPayload {
		const {
			paymentSchedulerTemplateId,
			contractTemplateId,
			questionnaireTemplateId,
			data
		} = templateData;
		return {
			paymentSchedulerTemplateId:
				paymentSchedulerTemplateId === '-2' ||
				paymentSchedulerTemplateId === '-3'
					? null
					: paymentSchedulerTemplateId,
			paymentsStrategy:
				(paymentSchedulerTemplateId === '-2' && 'NO_SPLIT_PAYMENTS') ||
				(paymentSchedulerTemplateId === '-3' && 'NO_DUE_DATE') ||
				'BY_PAYMENT_SCHEDULER',
			questionnaireTemplateId:
				questionnaireTemplateId === '-1' ? null : questionnaireTemplateId,
			contractTemplateId:
				contractTemplateId === '-1' ? null : contractTemplateId,
			data
		};
	}

	mapLineItems(products: Array<Product>): Array<Product> {
		return products.map((product: Product, index: number): Product => ({
			...product,
			productPackageTemplateId:
				product.id || product.productPackageTemplateId || null,
			order: index,
			price: this.stringModifierService.convertAmountIntoNumber(product.price),
			mandatory:
				product.type === 'PACKAGE'
					? products.length > 1
						? false
						: product.mandatory
					: product.mandatory,
			taxType:
				product.type === 'GIFT'
					? this.quoteUpdateService.setTaxTypeForDiscount(
							products,
							product.taxType
					  )
					: product.taxType
		}));
	}

	findTemplateItemById(list: Array<ListItem>, id: string | number): ListItem {
		const [template] = list.filter(
			(item: ListItem): boolean => item.id === +id
		);
		let name = template.name;
		if (this.stringModifierService.isSelectedText(name)) {
			name = this.stringModifierService.cutSelectedText(name);
		}
		return { name, id: template.id };
	}

	addSelectedTemplate(list: Array<ListItem>, item): void {
		if (item && item.name) {
			item.name = this.stringModifierService.addSelectedText(item.name);
			list.push(item);
		}
	}

	assignCorrectTemplateList(
		templates: Array<ListItem>,
		template: ListItem
	): Array<ListItem> {
		const newTemplates = this.orderByName.transform(templates);

		if (template) {
			newTemplates.push({
				...template,
				name: this.stringModifierService.addSelectedText(template.name)
			});
		}
		return newTemplates;
	}

	shouldDiffBeChecked(
		chosenTemplate: QtTemplateListItem,
		selectedTemplate: QtTemplateListItem
	): boolean {
		if (!chosenTemplate && !selectedTemplate) {
			return false;
		}

		if (
			chosenTemplate &&
			selectedTemplate &&
			chosenTemplate.id === selectedTemplate.id
		) {
			return false;
		}
		return true;
	}

	validateSelectedTemplates(
		data: Partial<QtBaseData>,
		contractTemplates: Array<ListItem>,
		questionnaireTemplates: Array<ListItem>,
		type: QtTemplateType
	): boolean {
		const selectedContractTemplate =
			contractTemplates[contractTemplates.length - 1];
		const selectedQuestionnaireTemplate =
			questionnaireTemplates[questionnaireTemplates.length - 1];
		const contractFlag =
			this.stringModifierService.isSelectedText(
				selectedContractTemplate.name
			) && selectedContractTemplate.id.toString() === data.contractTemplateId;
		const questionnaireFlag =
			this.stringModifierService.isSelectedText(
				selectedQuestionnaireTemplate.name
			) &&
			selectedQuestionnaireTemplate.id.toString() ===
				data.questionnaireTemplateId;

		if (contractFlag && questionnaireFlag) {
			this.messageService.showToaster(
				'e_7',
				'quoteInvoiceTemplates',
				type === 'INVOICE' ? 'invoice' : 'quote'
			);
			return false;
		}

		if (contractFlag) {
			this.messageService.showToaster(
				'e_5',
				'quoteInvoiceTemplates',
				type === 'INVOICE' ? 'invoice' : 'quote'
			);
			return false;
		}

		if (questionnaireFlag) {
			this.messageService.showToaster(
				'e_6',
				'quoteInvoiceTemplates',
				type === 'INVOICE' ? 'invoice' : 'quote'
			);
			return false;
		}

		return true;
	}

	isTemplateNameValid(type: string, template: QtTemplateListItem): boolean {
		if (type === 'duplicate' && `${template.name} copy`.length > 100) {
			this.messageService.showToaster('e_8', 'quoteInvoiceTemplates');
			return;
		}
		return true;
	}

	validateQuoteTemplate(data: Partial<QtBaseData>): boolean {
		const { products, name } = data.data;

		if (!name || (name && !name.trim())) {
			this.messageService.showToaster('e_0', 'quoteInvoiceTemplates');
			return false;
		}

		let invalidProductType;
		if (
			products.find(
				(product: Product): boolean =>
					product.type === 'GIFT' && !product.mandatory
			) !== undefined
		) {
			this.messageService.showToaster('e_6', 'editQuote');
			return false;
		}
		if (
			products.find(
				(product: Product): boolean =>
					product.type === 'GIFT' && parseInt(product.quantity) !== 1
			) !== undefined
		) {
			this.messageService.showToaster('e_10', 'editQuote');
			return false;
		}
		if (
			products.find(
				(product: Product): boolean =>
					product.type === 'GIFT' && product.price >= 0
			) !== undefined
		) {
			this.messageService.showToaster('e_16', 'editQuote');
			return false;
		}
		if (
			products.find((product: Product): boolean => product.type === 'GIFT') !==
				undefined &&
			products.find(
				(product: Product): boolean => product.taxType !== products[0].taxType
			) !== undefined
		) {
			this.messageService.showToaster('e_13', 'editQuote');
			return false;
		}
		if (products.length === 0) {
			this.messageService.showToaster('e_1', 'quoteInvoiceTemplates');
			return false;
		}
		if (
			products.find((product: Product): boolean => {
				if (product.name === '') {
					invalidProductType = product.type;
					if (invalidProductType === 'GIFT') {
						invalidProductType = 'discount';
					} else if (invalidProductType === 'ADD_ON') {
						invalidProductType = 'product';
					}
					return true;
				}
			})
		) {
			this.messageService.showToaster(
				'e_0',
				'productsPackages',
				invalidProductType.toLowerCase()
			);
			return false;
		}
		if (
			products.find((product: Product): boolean => product.quantity <= 0) !==
			undefined
		) {
			this.messageService.showToaster('e_8', 'editQuote');
			return false;
		}
		if (
			products.find((product: Product): boolean => {
				const escapedProduct = product.description
					? this.validationService.trimHTML(product.description).trim()
					: '';

				if (
					product.type !== 'GIFT' &&
					(!product.description ||
						product.description.length === 0 ||
						!escapedProduct)
				) {
					invalidProductType =
						product.type.toUpperCase() === 'PACKAGE' ? 'package' : 'product';
					return true;
				}
			})
		) {
			this.messageService.showToaster('e_9', 'editQuote', invalidProductType);
			return false;
		}
		if (
			products.find((product: Product): boolean => {
				if (product.name.trim().length === 0) {
					if (product.type.toUpperCase() === 'GIFT') {
						invalidProductType = 'Discount';
					} else {
						invalidProductType =
							product.type.toUpperCase() === 'PACKAGE' ? 'Package' : 'Product';
					}
					return true;
				}
			})
		) {
			this.messageService.showToaster('e_14', 'editQuote', invalidProductType);
			return false;
		}
		if (
			products.find((product: Product): boolean => product.discount > 100) !==
			undefined
		) {
			this.messageService.showToaster('e_2', 'editQuote');
			return false;
		}

		const invalidCounter = document.querySelector('.counter.red');
		if (invalidCounter) {
			this.messageService.showToaster('e_6', 'productsPackages', '4000');
			return false;
		}

		if (+data.paymentSchedulerTemplateId == -1) {
			this.messageService.showToaster('e_18', 'editQuote');
			return false;
		}

		return true;
	}

	validateInvoiceTemplate(data: Partial<QtBaseData>): boolean {
		const { products, name } = data.data;

		if (products.length === 0) {
			this.messageService.showToaster('e_1', 'quoteInvoiceTemplates');
			return false;
		}
		const amount = data.summary.subTotal;
		const discount = data.summary.discountValue;
		const totalDue = data.summary.total;
		const discountType = null;
		const pageName = 'editInvoice';
		let invalidProductType;
		const messagePlace = pageName;

		if (!name || (name && !name.trim())) {
			this.messageService.showToaster('e_0', 'quoteInvoiceTemplates');

			return false;
		}

		if (this.invoiceUpdateService.isChosenProductsInvalid(products, pageName)) {
			return false;
		}

		if (discountType === 'Fixed' && amount - discount < 0) {
			this.messageService.showToaster('e_1', 'editInvoice');
			return false;
		}

		const isValidDiscountFixed = this.paymentsService.isValidDiscountFixed(
			products,
			discountType
		);
		if (!isValidDiscountFixed) {
			return false;
		}

		const isValidDiscountPercent = this.paymentsService.isValidDiscountPercent(
			discount,
			discountType,
			pageName
		);
		if (!isValidDiscountPercent) {
			return false;
		}

		if (typeof totalDue === 'undefined' || totalDue === null) {
			this.messageService.showToaster('e_0', 'general');
			return false;
		}

		if (totalDue < 0) {
			this.messageService.showToaster('e_17', messagePlace);
			return false;
		}
		if (
			products.find(
				(product: Product): boolean =>
					product.type === 'GIFT' && product.price >= 0
			) !== undefined
		) {
			this.messageService.showToaster('e_16', 'editQuote');
			return false;
		}
		if (
			products.find((product: Product): boolean => {
				if (product.name === '') {
					invalidProductType = product.type;
					if (invalidProductType === 'GIFT') {
						invalidProductType = 'discount';
					} else if (invalidProductType === 'ADD_ON') {
						invalidProductType = 'product';
					}
					return true;
				}
			})
		) {
			this.messageService.showToaster(
				'e_0',
				'productsPackages',
				invalidProductType.toLowerCase()
			);
			return false;
		}
		if (
			products.find((product: Product): boolean => {
				if (product.name.trim().length === 0) {
					if (product.type.toUpperCase() === 'GIFT') {
						invalidProductType = 'Discount';
					} else {
						invalidProductType =
							product.type.toUpperCase() === 'PACKAGE' ? 'Package' : 'Product';
					}
					return true;
				}
			})
		) {
			this.messageService.showToaster('e_14', 'editQuote', invalidProductType);
			return false;
		}
		// MAGIC DON'T TOUCH
		if (
			products.filter((product: Product): boolean => product.type === 'GIFT')
				.length > 0 &&
			products.filter(
				(product: Product): boolean => product.taxType !== products[0].taxType
			).length > 0
		) {
			this.messageService.showToaster('e_4', 'payments');
			return false;
		}

		const invalidCounter = document.querySelector('.counter.red');
		if (invalidCounter) {
			this.messageService.showToaster('e_6', 'productsPackages', '4000');
			return false;
		}

		if (+data.paymentSchedulerTemplateId == -1) {
			this.messageService.showToaster('e_18', 'editQuote');
			return false;
		}

		return true;
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./shared/services/quote-invoice-templates/quote-invoice-templates.service.ts